import React from "react";
import { Tooltip } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import LeadsTable from "../Components/LeadsTable";
import { AtsCandidate } from "./AtsCandidates";

const PotentialTag: React.FC<{ potential: "High" | "Med" | "Low" }> = ({
    potential,
}) => {
    let color, icon;

    if (potential === "High") {
        color = "#2e7d32";
        icon = <ArrowUpwardIcon fontSize="small" />;
    } else if (potential === "Med") {
        color = "#DC6803";
        icon = <ArrowUpwardIcon fontSize="small" />;
    } else if (potential === "Low") {
        color = "#c62828";
        icon = <ArrowDownwardIcon fontSize="small" />;
    }

    return (
        <div
            style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "2px 8px",
                borderRadius: "16px",
                color,
                border: `1px solid ${color}`,
                fontSize: "0.75rem",
                fontWeight: 500,
            }}
        >
            {potential} Potential {icon}
        </div>
    );
};

interface AtsLeadsTableProps {
    leads: AtsCandidate[];
    totalProfiles: number;
    currentPage: number;
    setCurrentPage: (page: number) => void;
    sortConfig: { sortBy: string | null; sortOrder: 'asc' | 'desc' };
    onSortChange: (columnId: string) => void;
    onLeadClick: (candidate: AtsCandidate) => void;
}

const AtsLeadsTable: React.FC<AtsLeadsTableProps> = ({
    leads,
    totalProfiles,
    currentPage,
    setCurrentPage,
    sortConfig,
    onSortChange,
    onLeadClick,
}) => {
    const leadsPerPage = 10;

    return (
        <LeadsTable
            leads={leads}
            columns={[
                {
                    id: "sequence",
                    label: "#",
                    render: (candidate: AtsCandidate, index?: number) => (
                        <span style={{ fontWeight: 500, color: "#666" }}>
                            {(currentPage - 1) * leadsPerPage + (index ?? 0) + 1}
                        </span>
                    ),
                },
                {
                    id: "first_name",
                    label: "Name",
                    sortable: true,
                    render: (candidate: AtsCandidate) => (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                cursor: "pointer",
                                color: "#5175e0",
                                fontWeight: "bold",
                            }}
                            onClick={() => onLeadClick(candidate)}
                        >
                            {candidate.added_into_job && (
                                <Tooltip title="Already added to pool" arrow>
                                    <span
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            width: "20px",
                                            height: "20px",
                                            borderRadius: "50%",
                                            backgroundColor: "#2e7d32",
                                            color: "white",
                                            fontSize: "0.75rem",
                                            fontWeight: "bold",
                                            flexShrink: 0,
                                        }}
                                    >
                                        ✓
                                    </span>
                                </Tooltip>
                            )}
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                                {`${candidate.first_name} ${candidate.last_name}`}
                            </span>
                        </div>
                    ),
                },
                {
                    id: "email",
                    label: "Email",
                    sortable: true,
                    render: (candidate: AtsCandidate) =>
                        candidate.email_addresses?.[0]?.value || "N/A",
                },
                {
                    id: "title",
                    label: "Title",
                    render: (candidate: AtsCandidate) => (
                        <Tooltip title={candidate.title || ""} arrow>
                            <span>
                                {candidate.title?.length > 50
                                    ? `${candidate.title.substring(0, 50)}...`
                                    : candidate.title}
                            </span>
                        </Tooltip>
                    ),
                },
                {
                    id: "candidate_score",
                    label: "Match Score",
                    sortable: true,
                    render: (candidate: AtsCandidate) => (
                        <span style={{ fontWeight: 500 }}>
                            {candidate.evaluation_details?.candidate_score
                                ? `${Math.round(candidate.evaluation_details.candidate_score)}%`
                                : 'N/A'}
                        </span>
                    ),
                },
                {
                    id: "potential",
                    label: "Lead Potential",
                    render: (candidate: AtsCandidate) => (
                        <PotentialTag
                            potential={
                                candidate.evaluation_details?.candidate_score > 80
                                    ? "High"
                                    : candidate.evaluation_details?.candidate_score >= 50
                                        ? "Med"
                                        : "Low"
                            }
                        />
                    ),
                },
            ]}
            totalProfiles={totalProfiles}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            showCheckbox={false}
            sortConfig={sortConfig}
            onSortChange={onSortChange}
            highlightCondition={(lead: AtsCandidate) => lead.added_into_job === true}
        />
    );
};

export default AtsLeadsTable;
